import React, {PropsWithChildren, useContext, useRef} from 'react'

import {TransitionAsyncState, useTransition} from '../usetransition'
import {attachYmaps3React} from './attachYmaps3React'
import {loadYmapsApi} from './loadYmaps'
import {Props as ShowErrorProps, ShowError} from './ShowError'
import {MapRef, Maps} from './types'

export type MapsContextLoadedValue = {
  loaded: true
  ymaps3: Maps
  components: Awaited<ReturnType<typeof attachYmaps3React>>
  lang: string
}

type MapsContextValue = TransitionAsyncState<MapsContextLoadedValue, unknown> & {
  mapRef?: MapRef | null
  setMapRef?: (ref?: MapRef | null) => void
}

const MapsContext = React.createContext<MapsContextValue>({isPending: true})

const LOAD_MAPS_API_KEY = 'LOAD_MAPS_API_KEY'

export const useMapContext = () => useContext(MapsContext)

export const useMapContextValue = () => {
  const {payload: value} = useMapContext()

  return value
}

type MapsContextProviderProps = PropsWithChildren<
  {
    brand: 'yandex' | 'yango'
    lang: string
    mapRef: MapRef | null | undefined
    setMapRef: (ref: MapRef | null | undefined) => void
  } & ShowErrorProps
>

export const MapsContextProvider: React.FC<MapsContextProviderProps> = ({
  brand,
  errorMessage,
  children,
  lang,
  mapRef,
  setMapRef,
}) => {
  const [contextValue] = useTransition(() => loadYmapsApi({lang, brand}), {key: LOAD_MAPS_API_KEY})

  if (contextValue.error) {
    return <ShowError errorMessage={errorMessage} />
  }

  return <MapsContext.Provider value={{...contextValue, mapRef, setMapRef}}>{children}</MapsContext.Provider>
}
